import { api } from '@/api';
import {
  DeviceMeasurementErrorDto,
  instanceOfRunBeamConstancyMeasurementRequestDto,
  MeasurementStatusDto,
  ResultDto,
} from '@/api/generator';
import { AnyApiError, ApiError } from '@/errorHandling/typeGuards';
import { NotificationEntry } from '@/components/notifications/types';
import { notificationForDeviceError } from '@/components/notifications/utils';
import { injectStrict } from '@/utils/injectStrict';
import { useHubSubscription } from '@/utils/signalR';
import { HubConnectionBuilder } from '@microsoft/signalr';
import { useMemoize, createEventHook } from '@vueuse/core';
import { computed, MaybeRefOrGetter, Ref, ref, toRef, toValue, watch } from 'vue';
import { MeasurementEssentials } from '../types';
import { WorkflowCommunicationHubKey } from './types';
import { severityOrder, MAX_ACCEPTABLE_SEVERITY } from '@/components/deviceStatus/utils/severity';
import { truthy } from '@/utils/helper';

export function hasUnacceptableErrors(messages: DeviceMeasurementErrorDto[]): boolean {
  if (!messages?.length) return false;

  return messages.some((message) => {
    if (!message.severity) return false;

    const maxAcceptableSeverityLevel = severityOrder.indexOf(MAX_ACCEPTABLE_SEVERITY);
    const severityIndex = severityOrder.indexOf(message.severity);
    // severityOrder is sorted from highest to lowest severity
    return severityIndex >= 0 && severityIndex < maxAcceptableSeverityLevel;
  });
}

export function getWorkflowCommunicationHubConnectionSignalR() {
  const interval = 1; // in seconds

  const connection = new HubConnectionBuilder()
    .withUrl('/workflowCommunicationHub')
    .withAutomaticReconnect({
      nextRetryDelayInMilliseconds: () => interval * 1000,
    })
    .build();
  return connection;
}

export function useMeasurement<T extends MeasurementEssentials>(
  measurement: MaybeRefOrGetter<T | undefined>,
) {
  const measurementRef = toRef(measurement);

  const measurementId = ref<string | undefined>(undefined);

  const streamId = ref<string | undefined>(undefined);

  const status = useMeasurementStatusSignalR(streamId);

  const finishHook = createEventHook<ResultDto | undefined>();

  const messages = ref<DeviceMeasurementErrorDto[]>([]);

  const startMeasurementError = ref<DeviceMeasurementErrorDto>();

  watch(
    () => status.value?.messages,
    (newMessages) => {
      if (newMessages) {
        messages.value = newMessages;
      }
    },
    {
      immediate: true,
    },
  );

  const notifications = computed<NotificationEntry[]>(() => {
    return [startMeasurementError.value, ...messages.value].filter(truthy).map((message) => ({
      ...notificationForDeviceError(message),
      closable: false,
    }));
  });

  const isMeasuring = computed(() => {
    switch (status.value?.measurementStatus) {
      case 'BeamDetected':
      case 'ProcessingResults':
        return true;
      default:
        return false;
    }
  });

  const cancelable = computed(
    () => !!status.value?.measurementStatus && status.value?.measurementStatus !== 'Finished',
  );

  // Payload for the 422 lives at `.exception.error` on the generated DTO.
  function startMeasurementErrorHandler(error: AnyApiError) {
    if (error.name === ApiError.deviceMeasurement) {
      startMeasurementError.value = error.error?.exception?.error;
    } else {
      return error;
    }
  }

  async function start() {
    const runMeasurementRequestDto = toValue(measurement)?.request;

    if (runMeasurementRequestDto) {
      const { result } = await api.bccMeasurementsApi.runMeasurement(
        {
          runMeasurementRequestDto,
        },
        [startMeasurementErrorHandler],
      );

      streamId.value = result?.measurementId;

      if (result && measurementRef.value) {
        measurementId.value = result.measurementId;
        startMeasurementError.value = undefined;
        return measurementId.value;
      }
    }
  }

  async function getResult() {
    if (measurementId.value) {
      const { result: measurementResult } = await api.resultsApi.getResultByMeasurementId({
        measurementId: measurementId.value,
      });

      return measurementResult;
    }
  }

  let startPromise: Promise<string | undefined> | undefined;

  async function safeStart() {
    startPromise = start();
    await startPromise;
  }

  async function safeCancel() {
    return startPromise?.then(cancel);
  }

  async function cancel(measurementId: string | undefined) {
    if (measurementId) {
      const { success } = await api.bccMeasurementsApi.stopMeasurement({
        measurementId: measurementId,
      });

      return success;
    } else {
      console.warn('Unable to cancel measurement, no measurementId, start might have failed');
    }
  }

  function reset() {
    streamId.value = undefined;
    measurementId.value = undefined;
    messages.value = [];
  }

  watch(
    () => status.value?.measurementStatus,
    async (newStatus) => {
      if (newStatus === 'Finished') {
        const measurementResult =
          measurementRef.value?.request &&
          instanceOfRunBeamConstancyMeasurementRequestDto(measurementRef.value.request)
            ? await getResult()
            : undefined;

        finishHook.trigger(measurementResult);
      }
    },
  );

  return {
    start: safeStart,
    cancel: safeCancel,
    reset,
    cancelable,
    status,
    isMeasuring,
    notifications,
    messages,
    onFinish: finishHook.on,
    measurement: measurementRef,
    measurementId,
    request: computed(() => toValue(measurement)?.request),
  };
}

const cachedStateValue = useMemoize((_deviceId: string | undefined) => {
  return ref<MeasurementStatusDto | undefined>();
});

function useMeasurementStatusSignalR(
  streamId: MaybeRefOrGetter<string | undefined>,
): Ref<MeasurementStatusDto | undefined> {
  const connection = injectStrict(WorkflowCommunicationHubKey);

  const id = toRef(streamId);

  useHubSubscription<MeasurementStatusDto>({
    connection,
    event: 'WorkflowMeasurementUpdated',
    key: id,
    onMessage: (value) => {
      if (value.measurementId) {
        cachedStateValue(value.measurementId).value = value;
      }
    },
    subscribe: (key) => connection.invoke('RegisterToWorkflow', key),
    unsubscribe: (token) => connection.invoke('UnregisterFromWorkflow', token),
  });

  return computed(() => {
    return cachedStateValue(id.value).value;
  });
}
